import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowRight, Box, Keyboard, Package, QrCode, Warehouse } from "lucide-react";
import { ScanBox } from "../../components/mobile/ScanBox";
import { Button } from "../../components/ui/button";

const KIND_META = {
    compartment: { icon: Warehouse, color: "text-primary", label: "Fach" },
    article: { icon: Package, color: "text-info", label: "Artikel" },
    asset: { icon: Box, color: "text-secondary", label: "Ausleihgegenstand" },
};

export default function ScanPage() {
    const navigate = useNavigate();
    const [result, setResult] = useState(null);
    const [unknownCode, setUnknownCode] = useState("");
    const [manualRequestKey, setManualRequestKey] = useState(0);

    // Auflösung passiert bereits in der ScanBox (inkl. Dedupe) --
    // hier kommt nur noch das fertige Ergebnis an.
    const handleScan = useCallback((raw, resolved) => {
        if (!resolved) {
            setResult(null);
            setUnknownCode(raw);
            return;
        }
        setUnknownCode("");
        setResult(resolved);
    }, []);

    const go = (target) => {
        if (target.prefill) {
            sessionStorage.setItem("pw:scan-prefill", JSON.stringify(target.prefill));
        }
        navigate(target.to);
    };

    const captureUnknownItem = () => {
        sessionStorage.setItem("pw:scan-prefill", JSON.stringify({ unknownCode }));
        navigate("/flows/unknown-item");
    };

    const Icon = result ? KIND_META[result.kind]?.icon || QrCode : QrCode;

    return (
        <section className="flex flex-col gap-5" data-testid="scan-page">
            <div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    Floor Mode
                </div>
                <h1 className="text-3xl font-bold tracking-tight text-foreground">Scan</h1>
                <p className="mt-1 text-xs text-muted-foreground">
                    QR-Code, EAN-Barcode oder Fach-Code einscannen.
                </p>
            </div>

            <ScanBox
                placeholder="Fach · Artikel · Ausleihgegenstand"
                onResolved={handleScan}
                continuous={true}
                testId="scan-box"
                openManualKey={manualRequestKey}
            />

            {unknownCode ? (
                <div
                    className="rounded-lg border border-warning/60 bg-warning/10 p-4"
                    role="alert"
                    data-testid="scan-unknown-recovery"
                >
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-foreground" />
                        <div>
                            <h2 className="font-semibold text-foreground">Code nicht zugeordnet</h2>
                            <p className="mt-1 text-sm text-muted-foreground">
                                Prüfe den Code oder nimm den Gegenstand zur Klärung auf.
                            </p>
                            <code className="mt-2 block break-all text-xs text-foreground">{unknownCode}</code>
                        </div>
                    </div>
                    <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                        <Button onClick={captureUnknownItem} data-testid="scan-unknown-capture">
                            Gegenstand aufnehmen
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => setManualRequestKey((key) => key + 1)}
                            data-testid="scan-unknown-retry"
                        >
                            <Keyboard className="mr-2 h-4 w-4" /> Code manuell eingeben
                        </Button>
                    </div>
                </div>
            ) : null}

            {result ? (
                <div
                    className="rounded-lg border border-primary/40 bg-card/60 p-4"
                    data-testid="scan-result"
                >
                    <div className="flex items-center gap-3">
                        <Icon className={`h-6 w-6 ${KIND_META[result.kind]?.color || ""}`} />
                        <div className="flex-1">
                            <div className="text-xs uppercase tracking-wider text-muted-foreground">
                                {KIND_META[result.kind]?.label || result.kind}
                            </div>
                            <div className="text-lg font-semibold text-foreground">
                                {result.label}
                            </div>
                            {result.subtitle ? (
                                <div className="text-xs text-muted-foreground">
                                    {result.subtitle}
                                </div>
                            ) : null}
                        </div>
                    </div>

                    {result.suggestedFlows?.length ? (
                        <div className="mt-4 flex flex-col gap-2">
                            <div className="text-xs uppercase tracking-wider text-muted-foreground">
                                Weiter mit
                            </div>
                            {result.suggestedFlows.map((sf, i) => (
                                <Button
                                    key={i}
                                    variant="outline"
                                    onClick={() => go(sf)}
                                    className="h-11 justify-between"
                                    data-testid={`scan-action-${i}`}
                                >
                                    <span>{sf.label}</span>
                                    <ArrowRight className="h-4 w-4" />
                                </Button>
                            ))}
                        </div>
                    ) : null}
                </div>
            ) : null}

            <div className="rounded-md border border-border/60 bg-card/40 p-3 text-xs text-muted-foreground">
                Unterstützte Formate: <code>platzwart://&lt;kind&gt;/&lt;id&gt;</code>,{" "}
                EAN-Barcodes (8-14 Stellen), Fach-Codes und interne Kennungen oder Seriennummern.
            </div>
        </section>
    );
}
